import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input, FieldWrapper } from '@/components/ui/Input';

export function LoginPage() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Signed in successfully.');
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        <div className="mb-10 flex flex-col items-center gap-3 text-center">
          <img
            src="/logo.png"
            alt=""
            width={256}
            height={256}
            className="h-12 w-12 rounded-md"
            decoding="async"
          />
          <h1 className="display-serif text-4xl tracking-tight text-fg">Vonlof Admin</h1>
          <p className="mt-1 text-sm text-fg-muted">Sign in with email and password.</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <FieldWrapper id="email" label="Email" required>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@vonlof.co"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9"
              />
            </div>
          </FieldWrapper>

          <FieldWrapper id="password" label="Password" required>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-9"
              />
            </div>
          </FieldWrapper>

          <Button type="submit" size="lg" className="w-full" loading={submitting}>
            Sign in
          </Button>
        </form>

        <p className="mt-8 text-center text-xs text-fg-subtle">
          Access is restricted. Contact your administrator if you need an account.
        </p>
      </div>
    </div>
  );
}